import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
} from "firebase/firestore";
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  updateProfile,
} from "firebase/auth";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";

const firebaseConfig = {
  apiKey: process.env.GATSBY_FIREBASE_API_KEY,
  authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
  storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.GATSBY_FIREBASE_APP_ID,
  measurementId: process.env.GATSBY_FIREBASE_MEASUREMENT_ID,
};

let app;
let db;
let auth;
let storage;

if (typeof window !== "undefined") {
  app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  auth = getAuth(app);
  storage = getStorage(app);
}

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

const POPUP_FAIL_CODES = new Set([
  "auth/popup-blocked",
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/operation-not-supported-in-this-environment",
  "auth/web-storage-unsupported",
]);

// Popup-first sign-in. Falls back to redirect when the popup is blocked,
// closed by user, or unsupported (Safari ITP / mobile). authError state in
// AuthContext surfaces the Firebase error code if both paths fail.
const loginWithGoogle = async () => {
  if (!auth) return null;
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[auth] popup sign-in failed:", err && err.code, err);
    if (err && POPUP_FAIL_CODES.has(err.code)) {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw err;
  }
};

const consumeRedirectResult = async () => {
  if (!auth) return null;
  try {
    const result = await getRedirectResult(auth);
    return result ? result.user : null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[auth] redirect sign-in failed:", err && err.code, err);
    return null;
  }
};

const logout = async () => {
  if (!auth) return;
  await signOut(auth);
};

const saveSkills = async (uid, assessments) => {
  if (!db || !uid) return;
  await setDoc(doc(db, "skills", uid), {
    userId: uid,
    assessments,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

const loadSkills = async (uid) => {
  if (!db || !uid) return null;
  const snap = await getDoc(doc(db, "skills", uid));
  return snap.exists() ? snap.data().assessments : null;
};

const saveCerts = async (uid, certs) => {
  if (!db || !uid) return;
  await setDoc(doc(db, "skills", uid), {
    userId: uid,
    certs,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

const loadCerts = async (uid) => {
  if (!db || !uid) return null;
  const snap = await getDoc(doc(db, "skills", uid));
  return snap.exists() ? (snap.data().certs || null) : null;
};

const saveProofs = async (uid, proofs) => {
  if (!db || !uid) return;
  await setDoc(doc(db, "skills", uid), {
    userId: uid,
    proofs,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

const loadProofs = async (uid) => {
  if (!db || !uid) return null;
  const snap = await getDoc(doc(db, "skills", uid));
  return snap.exists() ? (snap.data().proofs || null) : null;
};

const setPublicProfile = async (uid, payload) => {
  if (!db || !uid) return;
  await setDoc(doc(db, "publicProfiles", uid), {
    ...payload,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

const loadPublicProfile = async (uid) => {
  if (!db || !uid) return null;
  const snap = await getDoc(doc(db, "publicProfiles", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const removePublicProfile = async (uid) => {
  if (!db || !uid) return;
  await deleteDoc(doc(db, "publicProfiles", uid));
};

const ensureUserDoc = async (firebaseUser) => {
  if (!db || !firebaseUser) return null;
  const ref = doc(db, "users", firebaseUser.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      email: firebaseUser.email,
      role: "public",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { role: "public", tags: [] };
  }
  const data = snap.data();
  return {
    role: data.role || "public",
    tags: Array.isArray(data.tags) ? data.tags : [],
  };
};

const loadUserRole = async (uid) => {
  if (!db || !uid) return "public";
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? (snap.data().role || "public") : "public";
};

const listUsers = async () => {
  if (!db) return [];
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const setEmailMuteAll = async (uid, mute) => {
  if (!db || !uid) return;
  await updateDoc(doc(db, "users", uid), {
    "emailPrefs.muteAll": Boolean(mute),
    updatedAt: serverTimestamp(),
  });
};

const loadEmailPrefs = async (uid) => {
  if (!db || !uid) return null;
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? (snap.data().emailPrefs || null) : null;
};

const logAudit = async (action, target = {}, before = null, after = null) => {
  if (!db || !auth || !auth.currentUser) return;
  try {
    await addDoc(collection(db, "audit"), {
      action,
      actorUid: auth.currentUser.uid,
      actorEmail: auth.currentUser.email || null,
      targetType: target.type || null,
      targetId: target.id || null,
      before,
      after,
      at: serverTimestamp(),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[audit] log failed:", err && err.code);
  }
};

const listAudit = async (limitCount = 100) => {
  if (!db) return [];
  const snap = await getDocs(
    query(collection(db, "audit"), orderBy("at", "desc"))
  );
  return snap.docs.slice(0, limitCount).map((d) => ({ id: d.id, ...d.data() }));
};

// Subscribes to the system/labHealth document written by the checkLabHealth
// Cloud Function. cb receives the doc data when the doc exists, or null when
// it is absent. Returns an unsubscribe function.
export function subscribeLabHealth(cb) {
  if (!db) return () => {};
  return onSnapshot(
    doc(db, "system", "labHealth"),
    (snap) => cb(snap.exists() ? snap.data() : null),
    () => cb(null),
  );
}

export function subscribeUserNotifications(uid, cb, limitCount = 50) {
  if (!db || !uid) return () => {};
  const q = query(
    collection(db, "notifications", uid, "items"),
    orderBy("createdAt", "desc"),
    limit(limitCount),
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => cb([]),
  );
}

export async function markNotificationsRead(uid, ids) {
  if (!db || !uid || !ids || !ids.length) return;
  await Promise.all(
    ids.map((id) => updateDoc(doc(db, "notifications", uid, "items", id), { read: true })),
  );
}

// History of ingestLeads scheduled-job runs. Written server-side; the
// /sales-leads page reads the last few to show a status strip.
const listIngestRuns = async (limitCount = 5) => {
  if (!db) return [];
  const snap = await getDocs(
    query(collection(db, "ingestRuns"), orderBy("at", "desc")),
  );
  return snap.docs.slice(0, limitCount).map((d) => ({ id: d.id, ...d.data() }));
};

// Latest archiveStaleLeads sweep summaries. Written server-side; the
// /sales-leads page shows the most recent one as a status line.
const listArchiveRuns = async (limitCount = 1) => {
  if (!db) return [];
  const snap = await getDocs(
    query(collection(db, "archiveRuns"), orderBy("at", "desc")),
  );
  return snap.docs.slice(0, limitCount).map((d) => ({ id: d.id, ...d.data() }));
};

const updateUserRole = async (uid, role) => {
  if (!db || !uid) return;
  if (!["public", "client", "staff", "admin"].includes(role)) {
    throw new Error(`invalid role: ${role}`);
  }
  const ref = doc(db, "users", uid);
  const beforeSnap = await getDoc(ref);
  const before = beforeSnap.exists() ? { role: beforeSnap.data().role || "public" } : null;
  await updateDoc(ref, {
    role,
    updatedAt: serverTimestamp(),
  });
  logAudit("role.change", { type: "user", id: uid }, before, { role });
};

const addUserTag = async (uid, tag) => {
  if (!db || !uid || !tag) return;
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const before = snap.data().tags || [];
  if (before.includes(tag)) return;
  const after = [...before, tag];
  await updateDoc(ref, { tags: after, updatedAt: serverTimestamp() });
  logAudit("user.tag.add", { type: "user", id: uid }, { tags: before }, { tags: after });
};

const removeUserTag = async (uid, tag) => {
  if (!db || !uid || !tag) return;
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const before = snap.data().tags || [];
  if (!before.includes(tag)) return;
  const after = before.filter((t) => t !== tag);
  await updateDoc(ref, { tags: after, updatedAt: serverTimestamp() });
  logAudit("user.tag.remove", { type: "user", id: uid }, { tags: before }, { tags: after });
};

// Lab progress
const loadLabProgress = async (uid) => {
  if (!db || !uid) return null;
  const snap = await getDoc(doc(db, "labProgress", uid));
  return snap.exists() ? snap.data() : null;
};

const saveLabProgress = async (uid, data) => {
  if (!db || !uid) return;
  await setDoc(doc(db, "labProgress", uid), {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// Proof-of-work: certifications attached to career-path skills
// File stored at proofs/{uid}/{skillKey}/{filename}; metadata persisted via saveCerts
const uploadProofFile = async (uid, skill, file) => {
  if (!storage || !uid || !skill || !file) return null;
  const safeSkill = skill.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  const safeName = file.name.replace(/[^a-z0-9._-]+/gi, "_");
  const path = `proofs/${uid}/${safeSkill}/${Date.now()}_${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file);
  const url = await getDownloadURL(ref);
  return { path, url, name: file.name };
};

// Avatar library — admin curates a shared set of 8-bit / pixel-style
// avatars under avatars/library/; users pick one on /profile. The chosen
// URL is written to Firebase Auth's photoURL via updateProfile so every
// surface that already reads user.photoURL picks it up.

const listLibraryAvatars = async () => {
  if (!storage) return [];
  const ref = storageRef(storage, "avatars/library");
  try {
    const res = await listAll(ref);
    const items = await Promise.all(
      res.items.map(async (item) => ({
        path: item.fullPath,
        name: item.name,
        url: await getDownloadURL(item),
      })),
    );
    // Sort by filename so the operator can control order by naming
    // (e.g. 01-pixel-warrior.png, 02-pixel-mage.png).
    return items.sort((a, b) => a.name.localeCompare(b.name));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[storage] listLibraryAvatars failed", err && err.message);
    return [];
  }
};

const uploadLibraryAvatar = async (file) => {
  if (!storage || !file) return null;
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("avatar must be an image");
  }
  if (file.size > 2 * 1024 * 1024) {
    throw new Error("avatar must be under 2MB");
  }
  const safe = file.name.replace(/[^a-z0-9._-]+/gi, "_");
  const path = `avatars/library/${safe}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type });
  const url = await getDownloadURL(ref);
  return { path, url, name: safe };
};

const deleteLibraryAvatar = async (path) => {
  if (!storage || !path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[storage] deleteLibraryAvatar failed", err && err.message);
  }
};

const setUserAvatar = async (url) => {
  if (!auth || !auth.currentUser) return null;
  await updateProfile(auth.currentUser, { photoURL: url || null });
  await auth.currentUser.reload();
  return url || null;
};

const deleteProofFile = async (path) => {
  if (!storage || !path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[proof] delete failed:", err && err.code);
  }
};

// Time tracker entries — subcollection per user
const addTimeEntry = async (uid, entry) => {
  if (!db || !uid) return null;
  const ref = await addDoc(collection(db, "timeEntries", uid, "entries"), {
    status: "draft",
    ...entry,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

const setTimeEntryStatus = async (uid, entryId, status) => {
  if (!db || !uid || !entryId) return;
  if (!["draft", "submitted", "approved", "rejected"].includes(status)) {
    throw new Error(`invalid status: ${status}`);
  }
  await updateDoc(doc(db, "timeEntries", uid, "entries", entryId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

const listTimeEntries = async (uid) => {
  if (!db || !uid) return [];
  const snap = await getDocs(
    query(collection(db, "timeEntries", uid, "entries"), orderBy("date", "desc"))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const updateTimeEntry = async (uid, entryId, patch) => {
  if (!db || !uid || !entryId) return;
  await updateDoc(doc(db, "timeEntries", uid, "entries", entryId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
};

const deleteTimeEntry = async (uid, entryId) => {
  if (!db || !uid || !entryId) return;
  await deleteDoc(doc(db, "timeEntries", uid, "entries", entryId));
  logAudit("timeEntry.delete", { type: "timeEntry", id: entryId }, { uid }, null);
};

// Junior staffing
const listJuniors = async () => {
  if (!db) return [];
  const snap = await getDocs(collection(db, "juniors"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const updateJuniorStatus = async (id, status) => {
  if (!db || !id) return;
  await updateDoc(doc(db, "juniors", id), {
    status,
    updatedAt: serverTimestamp(),
  });
};

const addJunior = async (data) => {
  if (!db) return null;
  const ref = await addDoc(collection(db, "juniors"), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  logAudit("junior.create", { type: "junior", id: ref.id }, null, { name: data.name });
  return ref.id;
};

const updateJunior = async (id, patch) => {
  if (!db || !id) return;
  await updateDoc(doc(db, "juniors", id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
  logAudit("junior.update", { type: "junior", id }, null, patch);
};

const deleteJunior = async (id) => {
  if (!db || !id) return;
  await deleteDoc(doc(db, "juniors", id));
  logAudit("junior.delete", { type: "junior", id }, { id }, null);
};

// Projects
const listProjects = async () => {
  if (!db) return [];
  const snap = await getDocs(collection(db, "projects"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const loadProject = async (id) => {
  if (!db || !id) return null;
  const snap = await getDoc(doc(db, "projects", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const createProject = async (data) => {
  if (!db) return null;
  const ref = await addDoc(collection(db, "projects"), {
    ...data,
    assignedJuniorIds: data.assignedJuniorIds || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

const updateProject = async (id, patch) => {
  if (!db || !id) return;
  await updateDoc(doc(db, "projects", id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
};

const assignJuniorToProject = async (juniorId, projectId) => {
  if (!db || !juniorId) return;
  const juniorRef = doc(db, "juniors", juniorId);
  // Single transaction so concurrent assigns can't dup `assignedJuniorIds`.
  await runTransaction(db, async (tx) => {
    tx.update(juniorRef, {
      assignedProjectId: projectId || null,
      status: projectId ? "Assigned" : "Available",
      updatedAt: serverTimestamp(),
    });
    if (!projectId) return;
    const projRef = doc(db, "projects", projectId);
    const projSnap = await tx.get(projRef);
    if (!projSnap.exists()) return;
    const ids = projSnap.data().assignedJuniorIds || [];
    if (ids.includes(juniorId)) return;
    tx.update(projRef, {
      assignedJuniorIds: [...ids, juniorId],
      updatedAt: serverTimestamp(),
    });
  });
};

export {
  db, auth, storage, collection, getDocs, doc, setDoc, getDoc, onSnapshot, serverTimestamp,
  query, where, orderBy, limit, updateDoc, addDoc,
  loginWithGoogle, logout, onAuthStateChanged,
  consumeRedirectResult,
  saveSkills, loadSkills,
  saveCerts, loadCerts,
  saveProofs, loadProofs,
  setPublicProfile, loadPublicProfile, removePublicProfile,
  ensureUserDoc, loadUserRole, listUsers, updateUserRole, addUserTag, removeUserTag,
  setEmailMuteAll, loadEmailPrefs,
  logAudit, listAudit,
  listIngestRuns,
  listArchiveRuns,
  loadLabProgress, saveLabProgress,
  uploadProofFile, deleteProofFile,
  listLibraryAvatars, uploadLibraryAvatar, deleteLibraryAvatar, setUserAvatar,
  addTimeEntry, listTimeEntries, updateTimeEntry, deleteTimeEntry, setTimeEntryStatus,
  listJuniors, updateJuniorStatus, addJunior, updateJunior, deleteJunior,
  listProjects, loadProject, createProject, updateProject, assignJuniorToProject,
};
